import { detectTimezone } from '../platformLists.js'

/**
 * Demographics validation. Pure functions, no React, so the harness can check
 * them — and the midnight case below is exactly why that is worth doing.
 *
 * These 14 fields are not a formality: demo_wake_time and demo_bed_time generate
 * all 30 beep fire times at consent, demo_country keys the crisis resources, and
 * demo_pregnancy / demo_bereavement gate two instruments. A bad value here is a
 * broken schedule for five days, not a cosmetic problem.
 */

/**
 * Accepts HH:MM and HH:MM:SS.
 *
 * The seconds form is not hypothetical: demographics sent as "07:00" read back
 * from the API as "07:00:00". Rejecting that would have told a participant
 * "Enter a time" about the value they had just successfully saved — on the two
 * fields that generate all 30 beep fire times.
 */
export function toMinutes(hhmm) {
  if (typeof hhmm !== 'string') return null
  const match = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(hhmm)
  if (!match) return null
  const h = Number(match[1])
  const m = Number(match[2])
  const s = match[3] === undefined ? 0 : Number(match[3])
  if (h > 23 || m > 59 || s > 59) return null
  return h * 60 + m
}

/** The value an <input type="time"> can display: always HH:MM. */
export function toTimeInputValue(value) {
  const minutes = toMinutes(value)
  if (minutes === null) return ''
  const h = String(Math.floor(minutes / 60)).padStart(2, '0')
  const m = String(minutes % 60).padStart(2, '0')
  return `${h}:${m}`
}

/**
 * free_text fields can come back from the API as a single-element array
 * (demo_prior_dx sent as "anxiety" reads back as ["anxiety"]). Rendering that
 * straight into a text input would show the participant `["anxiety"]`.
 */
export function toTextValue(value) {
  if (Array.isArray(value)) return value.filter(Boolean).join(', ')
  if (value === null || value === undefined) return ''
  return String(value)
}

/**
 * A time range that may wrap past midnight.
 *
 * demo_bed_time is declared 20:00 to 02:00 with crosses_midnight: true, and that
 * is correct, not a typo. Read naively as min <= t <= max it is an empty range,
 * and every bedtime a participant could enter would be rejected.
 */
export function timeInRange(value, min, max) {
  const t = toMinutes(value)
  if (t === null) return false
  const lo = toMinutes(min)
  const hi = toMinutes(max)
  if (lo === null || hi === null) return true // an unbounded field is not our business
  if (lo <= hi) return t >= lo && t <= hi
  return t >= lo || t <= hi // wraps past midnight
}

export function isBlank(value) {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  return false
}

/**
 * Is a field asked at all, given the answers so far?
 *
 * `visible_when: {field, values}` on a field means it is asked only when an
 * EARLIER field holds one of those values (2026-09-11: the pregnancy question
 * is asked of participants female at birth only). A field that is not asked
 * is not required, is not rendered, and sends nothing; the server applies the
 * same rule and stores null for it whatever the client sends.
 */
export function visibleByRule(field, values) {
  const rule = field?.visible_when
  if (!rule || !rule.field) return true
  const parent = values?.[rule.field]
  return (Array.isArray(rule.values) ? rule.values : []).map(String).includes(String(parent ?? ''))
}

/** Is a field's conditional detail sub-field currently on screen? */
export function detailVisible(field, parentValue) {
  const detail = field?.detail
  if (!detail) return false
  const when = Array.isArray(detail.shown_when) ? detail.shown_when : []
  return when.includes(parentValue)
}

function validateOne(field, value, errors) {
  /*
   * A READ-ONLY FIELD IS NOT HERS TO ANSWER, SO IT IS NOT HERS TO FAIL.
   *
   * `demo_age` is derived server-side from the registration dob and the PUT
   * overwrites whatever the client sends, so validating it here checks a value
   * that does not decide anything. It cost a demo twice: the box showed the
   * derived 31 while this read '' and refused with "This one is needed" on a
   * control she cannot type into, and submit() returns on the first error - so
   * the funnel was closed with no way forward.
   *
   * Two attempts to fix it by feeding the value into form state both came back,
   * because they depended on whether /auth/me or config resolved first. This
   * does not depend on ordering at all: the server is the authority for these
   * fields, and the client stops pretending to be a second one.
   */
  if (field.readonly) return

  if (isBlank(value)) {
    if (field.required) errors[field.field_id] = 'This one is needed.'
    return
  }

  if (field.type === 'integer') {
    const n = Number(value)
    if (!Number.isFinite(n) || !Number.isInteger(n)) {
      errors[field.field_id] = 'Enter a whole number.'
    } else if (typeof field.min === 'number' && typeof field.max === 'number') {
      if (n < field.min || n > field.max) {
        errors[field.field_id] = `Enter a number between ${field.min} and ${field.max}.`
      }
    } else if (typeof field.min === 'number' && n < field.min) {
      errors[field.field_id] = `Enter ${field.min} or more.`
    } else if (typeof field.max === 'number' && n > field.max) {
      errors[field.field_id] = `Enter ${field.max} or less.`
    }
    return
  }

  if (field.type === 'time') {
    if (toMinutes(value) === null) {
      errors[field.field_id] = 'Enter a time.'
    } else if (!timeInRange(value, field.min, field.max)) {
      errors[field.field_id] = `Choose a time between ${field.min} and ${field.max}.`
    }
    return
  }

  // Stored value is the ISO 3166-1 alpha-2 code, because crisis_resources.json
  // is keyed on it. A display name here would break the crisis lookup silently.
  if (field.type === 'country') {
    if (!/^[A-Z]{2}$/.test(String(value))) errors[field.field_id] = 'Choose your country.'
    return
  }

  if (field.type === 'timezone') {
    if (typeof value !== 'string' || !value.includes('/')) {
      errors[field.field_id] = 'Choose your timezone.'
    }
    return
  }

  if (field.type === 'single_select') {
    if (Array.isArray(field.options) && field.options.length && !field.options.includes(value)) {
      errors[field.field_id] = 'Choose one of the options.'
    }
    return
  }

  if (field.type === 'multi_select') {
    if (!Array.isArray(value)) {
      errors[field.field_id] = 'Choose one or more.'
    } else if (Array.isArray(field.options) && field.options.length) {
      const unknown = value.filter((v) => !field.options.includes(v))
      if (unknown.length) errors[field.field_id] = 'Choose from the options listed.'
    }
  }
}

/**
 * @returns {Record<string, string>} field_id -> message. Empty means valid.
 */
export function validateDemographics(fields, values) {
  const errors = {}

  for (const field of fields ?? []) {
    // Not asked means not hers to fail. Same rule as the detail sub-field.
    if (!visibleByRule(field, values)) continue
    validateOne(field, values?.[field.field_id], errors)

    // demo_current_meds carries a nullable detail sub-field, only required when
    // it is actually on screen. An invisible field can never block a submit.
    if (detailVisible(field, values?.[field.field_id])) {
      validateOne(field.detail, values?.[field.detail.field_id], errors)
    }
  }

  return errors
}

/** Initial form state: config's defaults plus the device timezone, nothing invented. */
export function initialValues(fields) {
  const values = {}
  for (const field of fields ?? []) {
    if (field.default !== undefined) values[field.field_id] = field.default
    else if (field.type === 'multi_select') values[field.field_id] = []
    else if (field.type === 'timezone') values[field.field_id] = detectTimezone()
    else values[field.field_id] = ''

    if (field.detail) values[field.detail.field_id] = ''
  }
  return values
}
